import dayjs from "dayjs"
import { convertToRaw, EditorState } from "draft-js"
// @ts-ignore
import draftToHtml from "draftjs-to-html"
import { cloneDeep, isEqual } from "lodash-es"
import { action, computed, makeObservable, observable, runInAction, toJS, untracked } from "mobx"

import EDDApi from "@/api/settings/preEDD"
import { DefaultRuleI, RuleItem, RulesDataType, WidgetData } from "@/api/settings/type/preEdd"
import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import { IContextualSaveBar } from "@/models/ICommonStore"
import { languageType } from "@/pages/Settings/EmailEdit/type"
import { getLanguagesList } from "@/pages/Settings/EmailEdit/utils"
import { RulesType } from "@/pages/Settings/PrePurchaseEDD/type"
import { isDraftEditorEmpty, stringToEditorState } from "@/pages/Settings/PrePurchaseEDD/utils"
import {
  initModulesStore,
  registerListener,
  registerModuleListener,
  setInitOptions,
} from "@/pages/Settings/PrePurchaseEDD/utils/listener"
import WidgetListener from "@/pages/Settings/PrePurchaseEDD/utils/widgetListener"
import stores from "@/stores"
import { defaultEDDModuleConfig } from "@/stores/tracking_page/defaultConfig"

function splitArrayIntoChunks(array:any[], chunkSize: number) {
  const result = []

  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize))
  }
  return result
}

function removeItems(arr: RuleItem[], idsToRemove: string[]) {
  return arr.filter(item => !idsToRemove.includes(item.gid.toString()))
}

function removeTrailingNewline(str: string) {
  if (str.endsWith("\n")) {
    return str.slice(0, -1) // 去掉最后一个字符
  }
  return str // 如果没有换行符，返回原字符串
}

function filterListByKeywordFunction(keyword: string) {
  return (item: any) => {
    if (!item.title) {
      return false
    }
    return item.title.toLowerCase().includes(keyword.toLowerCase())
  }
}

export default class PrePurchaseEDD {
  @observable loadedDataTime = 0
  @observable widgetInitialed = false
  @observable widgetLoading = false
  // app block开启状态
  @observable appBlockActive = false
  // 添加链接
  @observable addEddWidgetLink = ""

  @observable public saveBar: IContextualSaveBar = {
    disabled: false,
    loading: false,
    show: false,
    unlocking: false,
  }
  @observable public widgetSaveBar: IContextualSaveBar = {
    disabled: false,
    loading: false,
    show: false,
    unlocking: false,
  }

  @observable public widgetData: WidgetData["widget"] = {
    enabled: false,
    app_block_active_state: false,
    localized_settings: {},
    default: {
      localized_settings: {},
      colors: {
        border: "",
      },
    },
  }

  // editorState数据集合，必须要准备sting map和editorState map两种存储
  @observable public editorStateMap: Record<string, EditorState | null> = {}

  // 当前选择语言
  @observable public activeLanguage:string = languageType.ENGLISH

  // 规则数据集加载中
  @observable public rulesLoading: boolean = false

  // 规则数据集
  @observable public rulesData: RulesDataType = {
    default_rule: { ...defaultEDDModuleConfig, non_business_day_periods: [] }, // 默认default
  }

  // specific rules列表的排序规则
  @observable public specificSortType: "Ascending" | "Descending" | null = null

  @action.bound
  public setSpecificSortType(value: "Ascending" | "Descending") {
    this.specificSortType = value
  }

  @observable public specificSearchValue: string = ""

  @action.bound
  public changeSpecificSearchValue(value: string) {
    this.specificSearchValue = value
  }

  @observable public specificScope: string[] = []
  @action.bound
  public changeSearchSpecificScope(value: string[]) {
    this.specificScope = value
  }

  @observable public exclusionSearchValue: string = ""

  @action.bound
  public changeExclusionSearchValue(value: string) {
    this.exclusionSearchValue = value
  }

  @observable public exclusionScope: string[] = []
  @action.bound
  public changeSearchExclusionScope(value: string[]) {
    this.exclusionScope = value
  }


  constructor() {
    makeObservable(this)
  }

  @action.bound
  public setSaveBar<K extends keyof IContextualSaveBar>(key: K, value: IContextualSaveBar[K]) {
    this.saveBar[key] !== value && (this.saveBar[key] = value)
  }

  @action.bound
  public setWidgetSaveBar<K extends keyof IContextualSaveBar>(key: K, value: IContextualSaveBar[K]) {
    this.widgetSaveBar[key] !== value && (this.widgetSaveBar[key] = value)
  }


  // 语言切换列表
  @computed get languagesList() {
    const list = getLanguagesList(Object.keys(this.widgetData.localized_settings), "en")

    return list.map(item => ({ label: item.name, value: item.value }))
  }

  // 语言切换列表
  @computed get activeLanguageLabel() {
    return this.languagesList.find(item => item.value === this.activeLanguage)?.label
  }
  // 语言切换列表
  @computed get activeBorderColor() {
    return this.widgetData.localized_settings[this.activeLanguage].colors.border
  }

  // 当前富文本值
  @computed get editorState() {
    return this.editorStateMap[this.activeLanguage]
  }

  // 当前是否能Widget Revert To Default
  @computed get idRetractable() {
    if (this.widgetData.default.localized_settings[this.activeLanguage]){

      const defaultData = {
        translations: cloneDeep(this.widgetData.default.localized_settings[this.activeLanguage].translations),
        colors: this.widgetData.default.colors,
      }
      const activeData = this.widgetData.localized_settings[this.activeLanguage]

      return isEqual(defaultData, activeData)
    } else {
      return true
    }
  }
  // 当前是否有为空的富文本值
  @computed get isDraftEditorEmptyDisabled() {
    for (const locale in this.editorStateMap){
      if (this.widgetData.localized_settings[locale].translations.content === ""){
        return true
      }
      if (this.editorStateMap[locale] && isDraftEditorEmpty(this.editorStateMap[locale])){
        return true
      }
    }
  }

  // rule 保存的数据
  @computed get ruleUpdateInput() {
    if (this.rulesData.default_rule?.non_business_day_periods.length) {
      this.rulesData.default_rule.non_business_day_periods = this.rulesData.default_rule?.non_business_day_periods
        .filter(v => v)
    }

    return this.rulesData
  }

  // 当前Specific规则总列表
  @computed get currentSpecificRules() {
    const rules = (this.rulesData.specific_rules || [])
      .filter(item => !item.status || item.status !== "ARCHIVED")
      .filter(filterListByKeywordFunction(this.specificSearchValue))

    const filterRules = this.specificScope.length === 1 ? rules.filter(item => this.specificScope.includes(String(item.scope))) : rules

    // 默认null时不排序
    if (!this.specificSortType) return filterRules


    return filterRules.sort(
      (ruleA, ruleB) => this.specificSortType === "Ascending"
        ? ruleA.processing_time - ruleB.processing_time
        : ruleB.processing_time - ruleA.processing_time)
  }

  // 当前Specific规则总列表
  @computed get specificProductCount() {
    const rules = this.rulesData.specific_rules || []

    return  rules.filter(item => !item.status || item.status !== "ARCHIVED")
      .filter(item => item.scope === 0)
      .filter(filterListByKeywordFunction(this.specificSearchValue))
      .length
  }
  // 当前Specific规则总列表
  @computed get specificCollectionCount() {
    const rules = this.rulesData.specific_rules || []

    return  rules.filter(filterListByKeywordFunction(this.specificSearchValue))
      .filter(item => item.scope === 1).length
  }

  // 当前Exclusion规则总列表
  @computed get currentExclusionRules() {
    const rules = (this.rulesData.exclusion_rules || [])
      .filter(item => !item.status || item.status !== "ARCHIVED")
      .filter(filterListByKeywordFunction(this.exclusionSearchValue))

    return this.exclusionScope.length === 1 ? rules.filter(item => this.exclusionScope.includes(String(item.scope))) : rules
  }

  // 当前Exclusion规则总列表
  @computed get exclusionProductCount() {
    const rules = this.rulesData.exclusion_rules || []

    return rules.filter(item => item.scope === 0)
      .filter(item => !item.status || item.status !== "ARCHIVED")
      .filter(filterListByKeywordFunction(this.exclusionSearchValue))
      .length
  }
  // 当前Exclusion规则总列表
  @computed get exclusionCollectionCount() {
    const rules = this.rulesData.exclusion_rules || []
    const filterRules = rules.filter(item => item.scope === 1).filter(filterListByKeywordFunction(this.exclusionSearchValue))

    return filterRules.length
  }

  @action.bound
  setData(key: string, data: any) {
    // @ts-ignore
    this[key] = data
  }

  /**
   * 获取AppBlockStatus状态
   * @param calls
   */
  @action.bound
  getAppBlockStatus(calls?:()=>void):void{
    EDDApi.getAppBlockStatus().then(({ data }) => {
      this.appBlockActive = data.app_block_active_state.enabled
      this.addEddWidgetLink = data.app_block_active_state.add_widget_link
    })
      .finally(() => calls && calls())
  }

  /**
   * 获取AppBlockStatus状态
   * @param key
   * @param value
   */
  @action.bound
  setEditorStateMap(key:string, value: EditorState | null):void{
    const editorStateMap:Record<string, EditorState | null> = Object.assign(this.editorStateMap)

    editorStateMap[key] = value
    this.editorStateMap = editorStateMap
  }
  /**
   * 获取AppBlockStatus状态
   * @param calls
   */
  @action.bound
  getWidgetConfig(calls?:()=>void):void{
    this.widgetLoading = true
    EDDApi.getWidgetConfig().then(({ data }) => {
      WidgetListener.destroyListener()
      this.widgetData = data.widget

      // 初始化editorStateList
      const { localized_settings, app_block_active_state } = data.widget
      const editorStateMap:Record<string, EditorState | null> = {}

      Object.keys(localized_settings).map((key) => {
        editorStateMap[key] = stringToEditorState(localized_settings[key].translations.content || "")
      })

      this.appBlockActive = app_block_active_state
      this.editorStateMap = editorStateMap
      this.widgetInitialed = true
      this.activeLanguage = stores.userInfoSore.data.primary_locale || Object.keys(localized_settings)[0]

      WidgetListener.setInitOptions(localized_settings)
      WidgetListener.registerListener()
    })
      .finally(() => {
        calls && calls()
        this.widgetLoading = false
      })
  }


  /**
   * 关闭弹窗初始化富文本内容
   */
  @action.bound
  cancelWidgetEdit():void{
    const localized_settings = WidgetListener.getInitOptions()
    const editorStateMap:Record<string, EditorState | null> = {}

    Object.keys(localized_settings).map((key) => {
      editorStateMap[key] = stringToEditorState(localized_settings[key].translations.content || "")
    })

    this.widgetData.localized_settings = localized_settings
    this.editorStateMap = editorStateMap
  }

  /**
   * 获取AppBlockStatus状态
   * @param calls
   */
  @action.bound
  getRules(calls?: () => void): void {
    this.rulesLoading = true
    Promise.all([
      EDDApi.getDefaultRule(),
      EDDApi.getExclusionRule(),
      EDDApi.getSpecificRule(),
    ])
      .then(async ([{ data: defaultRule }, { data: exclusionRule }, { data: specificRule }]) => {
        this.loadedDataTime = dayjs().unix()
        const specificRuleIds = specificRule.specific_rules?.map(item => item.gid)
        const exclusionRuleIds = exclusionRule.exclusion_rules?.map(item => item.gid)

        const specificRuleIdsChunkedArray = splitArrayIntoChunks(specificRuleIds || [], 250)
        const exclusionRuleIdsChunkedArray = splitArrayIntoChunks(exclusionRuleIds || [], 250)

        // 创建一个数组来存储所有的 Promise
        const promises: any[] = []

        specificRuleIdsChunkedArray.forEach((ids, index) => {
          const promise = ShopifyGraphql.getProductAndCollectionList(ids).then(({ data }) => {
            if (data?.nodes) {
              data.nodes.forEach((node, idIndex) => {
                const targetIndex = index * 250 + idIndex
                const targetRule = specificRule.specific_rules?.[targetIndex]

                if (node === null) {
                  return
                }

                if (targetRule) {
                  targetRule.title = node.title
                  targetRule.image = targetRule.scope ? node.image ? node.image.url : ""
                    : node.featuredMedia ? node.featuredMedia.preview.image.url : ""
                  targetRule.status = node.status
                }
              })
            }
          })

          promises.push(promise)
        })

        exclusionRuleIdsChunkedArray.forEach((ids, index) => {
          const promise = ShopifyGraphql.getProductAndCollectionList(ids).then(({ data }) => {
            if (data?.nodes) {
              data.nodes.forEach((node, idIndex) => {
                const targetIndex = index * 250 + idIndex
                const targetRule = exclusionRule.exclusion_rules?.[targetIndex]

                if (node === null) {
                  return
                }
                if (targetRule) {
                  targetRule.title = node.title
                  targetRule.image = targetRule.scope ? node.image ? node.image.url : ""
                    : node.featuredMedia ? node.featuredMedia.preview.image.url : ""
                  targetRule.status = node.status
                }
              })
            }
          })

          promises.push(promise)
        })

        // 等待所有的 Promise 完成
        await Promise.all(promises)

        const rulesData = {
          ...defaultRule,
          ...exclusionRule,
          ...specificRule,
        }

        this.rulesData = rulesData
        this.rulesLoading = false
        setInitOptions(rulesData)
        registerListener()

        initModulesStore()
        registerModuleListener()
      })
      .finally(() => calls && calls())
  }

  @action.bound
  setDefaultRule<T extends keyof DefaultRuleI>(key: T, value: DefaultRuleI[T]) {
    this.rulesData.default_rule && (this.rulesData.default_rule[key] = value)
  }

  @action.bound
  addExclusionRules(list: RuleItem[]) {
    this.rulesData.exclusion_rules = [...list.filter(item => item.checked), ...(this.rulesData.exclusion_rules || [])]
  }

  @action.bound
  addSpecificRules(list: RuleItem[]) {
    this.rulesData.specific_rules = [...list.filter(item => item.checked), ...(this.rulesData.specific_rules || [])]
  }

  // 编辑editSpecificRules action
  @action.bound
  editSpecificRules(list: RuleItem[]) {
    const specific_rules = [...(this.rulesData.specific_rules || [])]

    // 替换、插入或删除项
    list.forEach(item => {
      const index = specific_rules.findIndex(rule => rule.gid === item.gid)

      if (item.checked) {
        // 如果 checked 为 true，处理替换或插入
        if (index !== -1) {
          if (item.processing_time === undefined){
            item.processing_time = specific_rules[index].processing_time
          }
          // 替换已有项
          specific_rules[index] = item
        } else {
          // 默认为1
          item.processing_time = 1
          // 插入到数组头部
          specific_rules.unshift(item)
        }
      } else {
        // 如果 checked 为 false，处理删除
        if (index !== -1) {
          specific_rules.splice(index, 1) // 删除项
        }
      }
    })

    // 更新 rulesData
    this.rulesData.specific_rules = specific_rules
  }

  @action.bound
  editExclusionRules(list: RuleItem[]) {
    const exclusion_rules = [...(this.rulesData.exclusion_rules || [])]

    // 替换、插入或删除项
    list.forEach(item => {
      const index = exclusion_rules.findIndex(rule => rule.gid === item.gid)

      if (item.checked) {
        // 如果 checked 为 true，处理替换或插入
        if (index !== -1) {
          // 替换已有项
          exclusion_rules[index] = item
        } else {
          // 插入到数组头部
          exclusion_rules.unshift(item)
        }
      } else {
        // 如果 checked 为 false，处理删除
        if (index !== -1) {
          exclusion_rules.splice(index, 1) // 删除项
        }
      }
    })

    // 更新 rulesData
    this.rulesData.exclusion_rules = exclusion_rules
  }


  /**
   * 设置Widget enabled设置
   * @param checked
   */
  @action.bound
  async setWidgetEnabled(checked: boolean){
    return new Promise((resolve, reject) => {
      EDDApi.setWidgetConfig({
        widget: { enabled: checked },
      }).then(() => {
        this.widgetData.enabled = checked
        resolve(checked)
      })
        .catch(() => {
          reject()
        })
    })
  }

  /**
   * 设置当前语言
   */
  @action.bound
  setActiveLanguage(code:string){
    this.activeLanguage = code
  }
  /**
   * 设置富文本的值
   */
  @action.bound
  onEditorStateChange(editorState: EditorState){
    // 需要同时更改translations中的String和存储的editorStateMap
    const base = removeTrailingNewline(this.widgetData.localized_settings[this.activeLanguage].translations.content)
    const now = removeTrailingNewline(draftToHtml(convertToRaw(editorState.getCurrentContent())))

    if (base !== now){
      this.widgetData.localized_settings[this.activeLanguage].translations.content = now
    }
    this.editorStateMap[this.activeLanguage] = editorState
  }
  /**
   * 设置富文本的值
   */
  @action.bound
  updateLocalizedSettings(){
    const { localized_settings } = this.widgetData

    const editorStateMap:Record<string, EditorState | null> = {}

    Object.keys(localized_settings).map((key) => {
      editorStateMap[key] = stringToEditorState(localized_settings[key].translations.content || "")
    })
    
    this.editorStateMap = editorStateMap
  }
  /**
   * 设置富文本的值
   */
  @action.bound
  onEditorHTMLChange(value: string){
    this.widgetData.localized_settings[this.activeLanguage].translations.content = value
  }
  /**
   * 设置富文本Border值
   */
  @action.bound
  onBorderColorChange(value: string){
    this.widgetData.localized_settings[this.activeLanguage].colors.border = value
  }
  /**
   * 富文本初始化
   */
  @action.bound
  onWidgetRevertToDefault(){
    const defaultConfig = cloneDeep(this.widgetData.default)
    const { translations } = defaultConfig.localized_settings[this.activeLanguage]

    this.widgetData.localized_settings[this.activeLanguage] = {
      translations: translations,
      colors: defaultConfig.colors,
    }
    this.editorStateMap[this.activeLanguage] = stringToEditorState(translations.content || "")
  }

  /**
   * Delete rules
   */
  @action.bound
  handleDeleteRules(type: RulesType, id: string[]) {
    if (type === RulesType.SpecificRule && this.rulesData.specific_rules) {
      this.rulesData.specific_rules = removeItems(this.rulesData.specific_rules, id)
    }
    if (type === RulesType.ExclusionRule && this.rulesData.exclusion_rules) {
      this.rulesData.exclusion_rules = removeItems(this.rulesData.exclusion_rules, id)
    }
  }
  /**
   * Delete all rules
   */
  @action.bound
  handleDeleteALLRules(type: RulesType) {
    if (type === RulesType.SpecificRule) {
      this.rulesData.specific_rules = []
    }
    if (type === RulesType.ExclusionRule) {
      this.rulesData.exclusion_rules = []
    }
  }

  @action.bound
  saveChangeSpecificRules(result: {
    add: RuleItem[],
    missing: string[],
    unchanged: string[],
    businessDays?: number,
  }) {
    let specificRules = this.rulesData.specific_rules || []

    // 移除操作
    specificRules = removeItems(specificRules, result.missing)

    // 修改操作
    if (result.businessDays !== undefined){
      specificRules.forEach(item => {
        if (result.unchanged.includes(item.gid)){
          item.processing_time = result.businessDays
        }
      })
    }

    // 添加操作
    this.rulesData.specific_rules = [...result.add, ...specificRules]
  }

  @action.bound
  saveChangeExclusionRules(result: {
    add: RuleItem[],
    missing: string[],
  }) {
    let exclusionRules = this.rulesData.exclusion_rules || []

    // 移除操作
    exclusionRules = removeItems(exclusionRules, result.missing)
    // 增加操作
    this.rulesData.exclusion_rules = [...result.add, ...exclusionRules]
  }

  @action.bound
  setAppBlockActive(status: boolean){
    this.appBlockActive = status
  }
}
